<template>
  <div>
    <quotes-send-modal ref="quoteSendModal" @sent="refreshQuote"/>

    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title v-if="quote">
        <strong>{{ $tc('quote', 1) }}</strong> #{{ quote.quote_number }}

        <status-chip
          small
          class="on-right"
          :status="quote.status"
          :color="statusColor"
          :clickable="user.isUserOfActiveTeam()"
          :committed="isCommitted"
          @cancel="handleCancel"
          @accepted="handleAccepted"
          @open="handleOpen"
        />
      </q-toolbar-title>

      <icon-bar :items="iconBarItems" />
    </q-toolbar>

    <div class="layout-padding">
      <div v-if="quote" class="row gutter-sm">
        <div class="col-12 col-lg-6 col-xl-7">
          <document-viewer :document="document" />
        </div>

        <div class="col-12 col-lg-6 col-xl-5">
          <q-tabs color="tertiary" underline-color="white">
            <q-tab slot="title" :label="$t('quote_meta')" name="meta" default />
            <q-tab slot="title" :label="$tc('note', 2)" name="notes" />

            <q-tab-pane name="meta" class="bg-white">
              <q-list no-border>
                <q-item class="invoice__invoice-date-entry">
                    <q-item-main :label="quoteDate" :sublabel="$t('quote_date')"/>
                  </q-item>

                  <q-item v-if="validUntil" class="invoice__invoice-date-entry">
                    <q-item-main :label="validUntil" :sublabel="$t('valid_until')"/>
                  </q-item>

                  <q-item class="invoice__invoice-date-entry" v-if="quote.invoices.data.length > 0">
                    <q-item-main>
                      <q-item-tile label>
                        <template v-for="invoice in quote.invoices.data">
                          <router-link
                            class="router-link"
                            v-bind:key="invoice.id"
                            :to="{ name: 'invoice', params: { teamId: activeTeam.id, invoice: invoice.id }}"
                          >
                            {{ invoice.invoice_number }}
                          </router-link>
                          &nbsp;
                        </template>
                      </q-item-tile>

                      <q-item-tile sublabel>{{ $tc('invoice', quote.invoices.data.length) }}</q-item-tile>
                    </q-item-main>
                  </q-item>

                  <q-item class="invoice__customer-name-entry">
                    <q-item-main>
                      <q-item-tile label>
                        <router-link
                          :to="{ name: 'customer', params: { customerId: customer.id, teamId: activeTeam.id } }"
                          class="router-link"
                          v-if="quote.customer_exists"
                        >
                          {{ customerName }}
                        </router-link>
                        <div v-else>
                          {{ customerName }}
                        </div>
                      </q-item-tile>

                      <q-item-tile sublabel>
                        {{ $tc('customer', 1) }}
                      </q-item-tile>
                    </q-item-main>
                  </q-item>

                <q-item class="invoice__due-date-entry" v-if="contactPersonName">
                  <q-item-main :label="contactPersonName" :sublabel="$tc('contact_person', 1)"/>
                </q-item>

                  <q-item v-if="quote.address" class="invoice__delivery-address-entry">
                    <q-item-main>
                      <q-item-tile v-html="quote.address.data.formatted_address" label/>

                      <q-item-tile sublabel>
                        {{ $t('address') }}
                      </q-item-tile>
                    </q-item-main>
                  </q-item>

                  <q-item v-if="createdBy" class="invoice__due-date-entry">
                    <q-item-main
                      :label="[createdBy.first_name,createdBy.last_name].join(' ')"
                      :sublabel="$t('quote_created_by')"
                    />
                  </q-item>

                  <q-item class="invoice__project-entry" v-if="quote.project_id">
                    <q-item-main>
                      <q-item-tile label>
                        <router-link
                          :to="{ name: 'project', params: { project: quote.project_id, teamId: activeTeam.id } }"
                          class="router-link">
                          {{ quote.project.data.name }}
                        </router-link>
                      </q-item-tile>

                      <q-item-tile sublabel>
                        {{ $tc('project', 1) }}
                      </q-item-tile>
                    </q-item-main>
                  </q-item>
              </q-list>
            </q-tab-pane>

            <q-tab-pane name="notes" class="bg-white">
              <note-input
                :notes="notes"
                :createNoteMethod="handleCreateNote"
                :updateNoteMethod="handleUpdateNote"
                :deleteNoteMethod="handleDeleteNote"
                hideEmptyState
                style="height: 250px"
              />
            </q-tab-pane>
            <q-tab-pane name="tab-5">Tab Five</q-tab-pane>
          </q-tabs>

          <q-card v-if="activities" class="bg-white q-mt-lg">
            <q-card-main>
              <timeline :activities="activities" />
            </q-card-main>
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import DocumentViewer from 'components/Documents/DocumentViewer.vue'
  import FileSaver from 'file-saver'
  import IconBar from 'components/Common/IconBar'
  import { NoteClient } from 'src/api/Notes'
  import { NoteInput } from 'components/Common/Inputs/NoteInput'
  import { QuoteClient } from 'src/api/Quotes'
  import QuotesSendModal from 'components/Quotes/QuoteSendModal.vue'
  import StatusChip from 'components/Quotes/StatusChip.vue'
  import Timeline from 'components/Invoices/Timeline.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import { formatMoney } from 'src/utils/format'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import parseDate from 'date-fns/parse'
  import printJS from 'print-js'
  import { quotes as quotesConfig } from 'src/config'

  const INCLUDES = ['user', 'customer', 'address', 'payments', 'invoices', 'notes.user', 'documents', 'project', 'activities', 'contact_person']

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        quote: null,
        pdf: null
      }
    },

    computed: {
      ...mapGetters(['activeTeam', 'user']),

      contactPersonName () {
        if (this.quote.contact_person) {
          return `${this.quote.contact_person.data.first_name} ${this.quote.contact_person.data.last_name}`
        }
      },

      createdBy () {
        if (!this.quote.user) {
          return null
        }

        return this.quote.user.data
      },

      includes () {
        return INCLUDES
      },

      quoteDate () {
        if (!this.quote.quote_date) {
          return ''
        }

        return date.formatDate(parseDate(this.quote.quote_date.date), this.$t('date_format'))
      },

      validUntil () {
        if (this.quote.valid_until) {
          return date.formatDate(parseDate(this.quote.valid_until.date), this.$t('date_format'))
        }
      },

      customer () {
        return this.quote.customer.data
      },

      customerName () {
        return getCustomerName(this.customer)
      },

      hasNotes () {
        return this.quote.hasOwnProperty('notes')
      },

      notes () {
        return this.hasNotes ? this.quote.notes.data : []
      },

      statusColor () {
        return quotesConfig.status_colors[this.quote.status]
      },

      document () {
        return this.quote.documents.data.find((document) => document.type === 'with_template')
      },

      isCommitted () {
        return this.quote ? this.quote.committed_at !== null : false
      },

      iconBarItems () {
        let items = [
          {
            text: 'duplicate',
            icon: 'file_copy',
            action: this.handleCopyQuote,
            restricted: true
          },
          {
            text: 'send_as_mail',
            icon: 'email',
            action: this.handleSend,
            restricted: true
          },
          {
            text: 'download_pdf',
            icon: 'cloud_download',
            action: this.handlePdfDownload
          },
          {
            text: 'print',
            icon: 'print',
            action: this.handlePdfPrint
          }
        ]

        if (!this.isCommitted) {
          items.unshift({
            text: 'commit',
            icon: 'lock',
            action: this.handleCommit,
            restricted: true
          }, {
            text: 'edit',
            icon: 'edit',
            action: this.handleEdit,
            restricted: true
          })
        }

        items.unshift({
          text: 'create_invoice',
          action: this.handleCreateInvoice,
          restricted: true
        })

        return items
      },

      activities () {
        const activities = this.quote.activities ? [ ...this.quote.activities.data ] : []
        return activities.sort((a, b) => {
          if (a.created_at.date !== b.created_at.date) {
            return a.created_at.date > b.created_at.date ? -1 : 1
          }

          return a.id > b.id ? -1 : 1
        })
      }
    },

    methods: {
      ...mapActions(['commitQuote']),

      handlePdfDownload () {
        const blob = new Blob([this.pdf], { type: 'application/pdf' })
        FileSaver.saveAs(blob, this.$tc('quote', 1) + '_' + this.quote.quote_number + '.pdf')
      },

      handlePdfPrint () {
        this.$q.loading.show()

        // Load the quote without the template applied
        new QuoteClient()
          .pdf(this.quote.id, false)
          .then(response => {
            this.$q.loading.hide()

            const base64 = btoa(String.fromCharCode(...new Uint8Array(response.data)))
            printJS({ printable: base64, type: 'pdf', base64: true })
          })
          .finally(() => this.$q.loading.hide())
      },

      handleEdit () {
        this.$router.push({
          name: 'edit-quote',
          params: { teamId: this.activeTeam.id, quote: this.quote.id }
        })
      },

      handleCommit () {
        this.$q.dialog({
          title: this.$t('confirm_commit_quote_title'),
          message: this.$t('confirm_commit_quote_body'),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => this.performCommit())
          .catch(() => {})
      },

      performCommit () {
        this.commitQuote(this.quote.id)
          .then(this.refreshQuote)
          .then(() => {
            this.$q.notify({ message: this.$t('quote_committed'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      refreshQuote () {
        return (new QuoteClient())
          .include(INCLUDES)
          .get(this.quote.id)
          .then(response => {
            this.quote = response.data.data[0]
          })
      },

      handleSend () {
        this.$refs.quoteSendModal.open(this.quote)
      },

      handleCopyQuote () {
        this.$router.push({
          name: 'create-quote',
          params: { teamId: this.activeTeam.id },
          query: { copy: this.quote.id }
        })
      },

      handleCancel () {
        this.$q.dialog({
          title: this.$t('warning'),
          message: this.$t('confirm_quote_cancel_body'),
          ok: this.$t('ok'),
          cancel: this.$t('cancel')
        })
          .then(() => {
            return new QuoteClient()
              .include(INCLUDES)
              .setStatus(this.quote.id, { 'status_code': 'canceled' })
          })
          .then(response => {
            this.quote = response.data.data
          })
      },

      handleCreateInvoice () {
        this.$router.push({
          name: 'create-invoice',
          params: { teamId: this.activeTeam.id },
          query: { quote: this.quote.id }
        })
      },

      handleCreateNote (data) {
        return new QuoteClient()
          .include('user')
          .addNote(this.quote.id, data)
          .then(response => {
            const note = response.data.data

            if (this.hasNotes) {
              this.notes.push(note)
            } else {
              const notes = { data: [note] }
              this.$set(this.quote, 'notes', notes)
            }
          })
      },

      handleUpdateNote (noteId, data) {
        return new NoteClient()
          .include('user')
          .update(noteId, data)
          .then(response => {
            const updatedNote = response.data.data
            const key = this.quote.notes.data.find(note => {
              return note.id === noteId
            })
            this.quote.notes.data.splice(key, 1, updatedNote)
          })
      },

      handleDeleteNote (noteId) {
        return new NoteClient()
          .destroy(noteId)
          .then(response => {
            const key = this.quote.notes.data.find(note => {
              return note.id === noteId
            })
            this.quote.notes.data.splice(key, 1)
          })
      },

      handleAccepted () {
        return new QuoteClient()
          .include(INCLUDES)
          .setStatus(this.quote.id, { 'status_code': 'accepted' })
          .then(response => {
            this.quote = response.data.data
          })
          .catch(createErrorFromResponse)
      },

      handleOpen () {
        return new QuoteClient()
          .include(INCLUDES)
          .setStatus(this.quote.id, { 'status_code': 'open' })
          .then(response => {
            this.quote = response.data.data
          })
          .catch(createErrorFromResponse)
      }
    },

    filters: {
      date (value, format) {
        return date.formatDate(parseDate(value), format)
      },

      money (value, currency) {
        return formatMoney(value, currency)
      }
    },

    beforeRouteEnter (to, from, next) {
      const quoteId = to.params.quote

      let pdf = new QuoteClient()
        .pdf(quoteId)

      let quote = new QuoteClient()
        .include(INCLUDES)
        .get(quoteId)

      return Promise.all([pdf, quote])
        .then(values => {
          next(vm => {
            vm.pdf = values[0].data
            vm.quote = values[1].data.data[0]
          })
        })
    },

    beforeRouteUpdate (to, from, next) {
      const quoteId = to.params.quote

      let pdf = new QuoteClient()
        .pdf(quoteId)

      let quote = new QuoteClient()
        .include(INCLUDES)
        .get(quoteId)

      return Promise.all([pdf, quote])
        .then(values => {
          this.pdf = values[0].data
          this.quote = values[1].data.data[0]
          next()
        })
    },

    components: {
      NoteInput,
      QuotesSendModal,
      StatusChip,
      DocumentViewer,
      IconBar,
      Timeline
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .invoice__pdf
    width: 100%

    @media (max-width $breakpoint-sm-max)
      width: 100%

  .invoice__delivery-address-entry,
  .invoice__invoice-address-entry
    p
      margin: 0

</style>
